import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, ExternalLink } from "lucide-react";

/**
 * AnalyticsDrilldown
 * ------------------
 * A right-side sheet that opens when the admin clicks any KPI card on
 * /admin/analytics. Fetches the underlying items for the clicked category
 * from `GET /api/admin/analytics/drilldown/{category}` and renders them as
 * a compact table with links to the relevant admin pages when available.
 *
 * Categories → column layouts are colocated here (one small `render*Row`
 * function per shape) so the drawer stays declarative and each category's
 * table structure lives with the code that reads it.
 */

const TITLES = {
  users: "All users",
  vendors: "Vendors",
  organizers: "Organizers",
  pro_active: "Maker Pro subscribers",
  verified_organizers: "Verified organizers",
  events_total: "All events",
  events_live: "Live events",
  events_pending: "Events pending review",
  events_cancelled: "Cancelled events",
  applications_total: "All applications",
  applications_approved: "Approved applications",
  applications_pending: "Pending applications",
  ratings_total: "Ratings submitted",
  booth_fees: "Booth-fee payments",
  platform_fee: "Platform-fee earnings",
};

const centsToDollars = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined,
      { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

function TableHead({ cols }) {
  return (
    <thead>
      <tr>
        {cols.map((c) => (
          <th key={c} className="text-left text-[10px] uppercase tracking-widest py-2 px-2 font-normal"
              style={{ color: "var(--ink-soft)", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
            {c}
          </th>
        ))}
      </tr>
    </thead>
  );
}

function UserRows({ items }) {
  return (
    <table className="w-full text-sm" data-testid="drilldown-table-users">
      <TableHead cols={["Name", "Email", "Role", "Plan", "Joined"]} />
      <tbody>
        {items.map((u) => (
          <tr key={u.id} data-testid={`drilldown-row-${u.id}`}
              className="hover:bg-[rgba(107,47,186,0.04)]">
            <td className="py-2 px-2 border-b border-black/5">
              <Link to={`/admin/users/${u.id}`}
                    className="inline-flex items-center gap-1 hover:underline"
                    style={{ color: "var(--avt-purple)" }}>
                {u.business_name || u.name || "—"} <ExternalLink size={11} />
              </Link>
              {u.signup_intent && u.signup_intent !== u.role && (
                <span className="ml-2 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: "rgba(249,115,22,0.14)", color: "var(--avt-orange)" }}>
                  {u.signup_intent}
                </span>
              )}
            </td>
            <td className="py-2 px-2 border-b border-black/5 text-xs" style={{ color: "var(--ink-soft)" }}>{u.email}</td>
            <td className="py-2 px-2 border-b border-black/5 text-xs capitalize">{u.role}</td>
            <td className="py-2 px-2 border-b border-black/5 text-xs">
              <span className="uppercase tracking-widest text-[10px] px-1.5 py-0.5 rounded"
                    style={{
                      backgroundColor: u.plan === "pro" ? "rgba(196,139,46,0.16)" : "rgba(0,0,0,0.05)",
                      color: u.plan === "pro" ? "#8a5a10" : "var(--ink-soft)",
                    }}>
                {u.plan}
              </span>
            </td>
            <td className="py-2 px-2 border-b border-black/5 text-xs" style={{ color: "var(--ink-soft)" }}>
              {fmtDate(u.created_at)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EventRows({ items }) {
  return (
    <table className="w-full text-sm" data-testid="drilldown-table-events">
      <TableHead cols={["Title", "Organizer", "City", "Date", "Status"]} />
      <tbody>
        {items.map((e) => (
          <tr key={e.id} data-testid={`drilldown-row-${e.id}`}
              className="hover:bg-[rgba(107,47,186,0.04)]">
            <td className="py-2 px-2 border-b border-black/5">
              <Link to={`/events/${e.id}`}
                    className="inline-flex items-center gap-1 hover:underline"
                    style={{ color: "var(--avt-purple)" }}>
                {e.title} <ExternalLink size={11} />
              </Link>
            </td>
            <td className="py-2 px-2 border-b border-black/5 text-xs">{e.organizer}</td>
            <td className="py-2 px-2 border-b border-black/5 text-xs">{e.city || "—"}</td>
            <td className="py-2 px-2 border-b border-black/5 text-xs">{fmtDate(e.date)}</td>
            <td className="py-2 px-2 border-b border-black/5 text-xs capitalize">
              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                    style={{
                      backgroundColor: e.status === "live" ? "rgba(132,204,22,0.14)"
                        : e.status === "pending_review" ? "rgba(249,115,22,0.14)"
                        : "rgba(0,0,0,0.06)",
                      color: e.status === "live" ? "#4B7A0A"
                        : e.status === "pending_review" ? "var(--avt-orange)"
                        : "var(--ink-soft)",
                    }}>
                {e.status}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ApplicationRows({ items }) {
  return (
    <table className="w-full text-sm" data-testid="drilldown-table-apps">
      <TableHead cols={["Event", "Vendor", "Status", "Paid?", "When"]} />
      <tbody>
        {items.map((a) => (
          <tr key={a.id} data-testid={`drilldown-row-${a.id}`}
              className="hover:bg-[rgba(107,47,186,0.04)]">
            <td className="py-2 px-2 border-b border-black/5 text-sm">
              {a.event_id ? (
                <Link to={`/events/${a.event_id}`} className="hover:underline"
                      style={{ color: "var(--avt-purple)" }}>{a.event}</Link>
              ) : a.event}
            </td>
            <td className="py-2 px-2 border-b border-black/5 text-xs">{a.vendor}</td>
            <td className="py-2 px-2 border-b border-black/5 text-xs capitalize">{a.status}</td>
            <td className="py-2 px-2 border-b border-black/5 text-xs" style={{ color: "var(--ink-soft)" }}>
              {a.payment_status || "—"}
            </td>
            <td className="py-2 px-2 border-b border-black/5 text-xs" style={{ color: "var(--ink-soft)" }}>
              {fmtDate(a.created_at)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RatingRows({ items }) {
  return (
    <table className="w-full text-sm" data-testid="drilldown-table-ratings">
      <TableHead cols={["Overall", "Direction", "Comment", "When"]} />
      <tbody>
        {items.map((r) => (
          <tr key={r.id} data-testid={`drilldown-row-${r.id}`}
              className="hover:bg-[rgba(107,47,186,0.04)]">
            <td className="py-2 px-2 border-b border-black/5 text-sm font-semibold">
              {r.overall}★
            </td>
            <td className="py-2 px-2 border-b border-black/5 text-xs capitalize">{r.direction || "—"}</td>
            <td className="py-2 px-2 border-b border-black/5 text-xs italic" style={{ color: "var(--ink-soft)" }}>
              {r.comment ? `“${r.comment}”` : "—"}
            </td>
            <td className="py-2 px-2 border-b border-black/5 text-xs" style={{ color: "var(--ink-soft)" }}>
              {fmtDate(r.created_at)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PaymentRows({ items, category }) {
  const showFee = category === "platform_fee";
  return (
    <table className="w-full text-sm" data-testid="drilldown-table-payments">
      <TableHead cols={["Amount", showFee ? "Platform fee" : "Booth fee", "Event", "Vendor", "When"]} />
      <tbody>
        {items.map((tx) => (
          <tr key={tx.id} data-testid={`drilldown-row-${tx.id}`}
              className="hover:bg-[rgba(107,47,186,0.04)]">
            <td className="py-2 px-2 border-b border-black/5 text-sm font-semibold">
              {centsToDollars(tx.amount_cents)}
            </td>
            <td className="py-2 px-2 border-b border-black/5 text-sm"
                style={{ color: showFee ? "var(--avt-purple)" : "var(--ink-soft)" }}>
              {centsToDollars(showFee ? tx.platform_fee_cents : tx.amount_cents)}
            </td>
            <td className="py-2 px-2 border-b border-black/5 text-xs">{tx.event}</td>
            <td className="py-2 px-2 border-b border-black/5 text-xs">{tx.vendor}</td>
            <td className="py-2 px-2 border-b border-black/5 text-xs" style={{ color: "var(--ink-soft)" }}>
              {fmtDate(tx.created_at)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function AnalyticsDrilldown({ category, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !category) return;
    setLoading(true);
    setData(null);
    setErr("");
    api.get(`/admin/analytics/drilldown/${category}?limit=200`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not load drilldown"))
      .finally(() => setLoading(false));
  }, [open, category]);

  const title = TITLES[category] || category;
  const items = data?.items || [];

  const renderTable = () => {
    if (["users", "vendors", "organizers", "pro_active", "verified_organizers"].includes(category)) {
      return <UserRows items={items} />;
    }
    if (["events_total", "events_live", "events_pending", "events_cancelled"].includes(category)) {
      return <EventRows items={items} />;
    }
    if (["applications_total", "applications_approved", "applications_pending"].includes(category)) {
      return <ApplicationRows items={items} />;
    }
    if (category === "ratings_total") return <RatingRows items={items} />;
    if (category === "booth_fees" || category === "platform_fee") {
      return <PaymentRows items={items} category={category} />;
    }
    return null;
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-3xl overflow-y-auto"
        data-testid="analytics-drilldown-sheet"
      >
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl">
            {title}
          </SheetTitle>
          <SheetDescription data-testid="drilldown-count">
            {loading ? "Loading…" : data ? `${data.count} record${data.count === 1 ? "" : "s"}` : ""}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          {loading && (
            <div className="py-12 text-center" data-testid="drilldown-loading">
              <Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: "var(--avt-purple)" }} />
            </div>
          )}
          {err && (
            <div className="text-sm rounded-md p-3" data-testid="drilldown-error"
                 style={{ backgroundColor: "rgba(249,115,22,0.10)", color: "var(--avt-orange)" }}>
              {err}
            </div>
          )}
          {!loading && !err && items.length === 0 && (
            <div className="py-8 text-center text-sm" data-testid="drilldown-empty"
                 style={{ color: "var(--ink-soft)" }}>
              No records yet.
            </div>
          )}
          {!loading && items.length > 0 && renderTable()}
        </div>
      </SheetContent>
    </Sheet>
  );
}
